import { useState, useMemo, useEffect, useRef } from "react"
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors, DragEndEvent, DragStartEvent } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable"
import useSites from "~/api2/hooks/useSites"
import { useCampingStyles } from "~/api2/hooks/useCampingStyles"
import { useCamp } from "~/api2/hooks/useCamp"
import SitesApi, { IUpdateProps } from "~/api2/sites"
import CampingStylesApi from "~/api2/camping_styles"
import { SiteProps } from "~/interfaces"
import SaveButtons from "../SaveButtons"
import SortableSite from "./SortableSite"
import SortableStyle from "./SortableStyle"
import Modal from "~/components/ui/Modal"
import Button from "~/components/ui/Button"
import EditSiteForm from "~/components/Admin/Reservations/EditSiteForm"
import { features, bedFeatures } from "~/components/Sites/SiteFeatures"
import { useNotification } from "~/contexts/NotificationProvider"

export default function Sites() {
  const {
    index: { data: sitesData, refetch: refetchSites },
  } = useSites()
  const {
    index: { data: styles },
  } = useCampingStyles()
  const {
    index: { data: camp },
  } = useCamp()
  const { showNotification } = useNotification()
  const [sites, setSites] = useState<SiteProps[]>([])
  const [originalSites, setOriginalSites] = useState<SiteProps[]>([])
  const [styleOrder, setStyleOrder] = useState<string[]>([])
  const [originalStyleOrder, setOriginalStyleOrder] = useState<string[]>([])
  const [pending, setPending] = useState(false)
  const [confirm, setConfirm] = useState<{ action: "duplicate" | "remove", site: SiteProps } | null>(null)
  const [editingSite, setEditingSite] = useState<SiteProps | null>(null)
  const [isDraggingStyle, setIsDraggingStyle] = useState(false)
  const [isStuck, setIsStuck] = useState(false)
  const saveButtonsRef = useRef<HTMLDivElement>(null)
  const editSiteFormRef = useRef<{ handleSubmit: (cb: (data: any) => void) => void }>(null)

  const isDirty = useMemo(() => {
    if (sites.length !== originalSites.length) return true
    const siteChanged = sites.some((s) => originalSites.find((o) => o.id === s.id)?.sort !== s.sort)
    const styleChanged = styleOrder.some((id, idx) => originalStyleOrder[idx] !== id)
    return siteChanged || styleChanged
  }, [sites, originalSites, styleOrder, originalStyleOrder])

  useEffect(() => {
    if (sitesData) {
      const sorted = [...sitesData].sort((a, b) => a.sort - b.sort)
      setSites(sorted)
      setOriginalSites(sorted)
    }
  }, [sitesData])

  useEffect(() => {
    if (styles) {
      const ordered = styles
        .slice()
        .sort((a, b) => a.sort - b.sort)
        .map((s) => s.id)
      setStyleOrder(ordered)
      setOriginalStyleOrder(ordered)
    }
  }, [styles])

  useEffect(() => {
    const sentinel = document.getElementById('save-buttons-sentinel')
    if (!sentinel) return

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setIsStuck(!entry.isIntersecting)
      },
      {
        root: null,
        threshold: 0,
      }
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [])

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const groups = useMemo(() => {
    if (!styles) return []
    return styleOrder.map((id) => {
      const style = styles.find((s) => s.id === id)!
      return {
        style,
        sites: sites.filter((s) => s.camping_style_id === style?.id).sort((a, b) => a.sort - b.sort),
      }
    })
  }, [styles, sites, styleOrder])

  const handleDragEnd = (event: DragEndEvent, styleId: string) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setSites((prev) => {
      const updated = [...prev]
      const subset = updated.filter((s) => s.camping_style_id === styleId)
      const ids = subset.map((s) => s.id)
      const oldIndex = ids.indexOf(String(active.id))
      const newIndex = ids.indexOf(String(over.id))
      const moved = arrayMove(subset, oldIndex, newIndex)

      const reorderedSites: typeof updated = []
      let globalSortCounter = 1

      styleOrder.forEach(campingStyleId => {
        const sitesForStyle = campingStyleId === styleId
          ? moved
          : updated.filter(s => s.camping_style_id === campingStyleId).sort((a, b) => a.sort - b.sort) // Keep original order for others

        sitesForStyle.forEach(site => {
          reorderedSites.push({ ...site, sort: globalSortCounter++ })
        })
      })

      return reorderedSites
    })
  }

  const handleStyleDragStart = (event: DragStartEvent) => {
    setIsDraggingStyle(true)
  }

  const handleStyleDragEnd = (event: DragEndEvent) => {
    setIsDraggingStyle(false)
    const { active, over } = event
    if (!over || active.id === over.id) return
    setStyleOrder((prev) => {
      const oldIndex = prev.indexOf(String(active.id))
      const newIndex = prev.indexOf(String(over.id))
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const handleSave = async () => {
    setPending(true)

    const siteUpdates = sites.reduce(
      (acc, site) => {
        acc[site.id] = site.sort
        return acc
      },
      {} as Record<string, number>,
    )
    const campingStyleUpdates = styleOrder.reduce(
      (acc, id, index) => {
        acc[id] = index + 1
        return acc
      },
      {} as Record<string, number>,
    )

    await Promise.all([SitesApi.batchUpdateSort(siteUpdates), CampingStylesApi.batchUpdateSort(campingStyleUpdates)])
    setOriginalSites([...sites])
    setOriginalStyleOrder([...styleOrder])

    setPending(false)
    refetchSites()
  }

  const handleEditSubmit = async (formData: any) => {
    console.log('Form data received:', formData)

    // Process prices data
    const pricesData = (formData.prices || []).map(price => ({
      id: price.id,
      duration: price.duration,
      amount_in_cents: Number(price.amount_in_cents) * 100,
      _destroy: !price.isActive,
    }))

    const vendorCalendarsData = (formData.vendor_calendars || []).map(vc => ({
      id: vc.id,
      kind: vc.kind,
      calendar_url: vc.calendar_url,
    }))

    const dataToSend: IUpdateProps = {
      name: formData.name,
      camping_style: formData.camping_style,
      kind: formData.kind,
      max_rig_length: formData.max_rig_length ? Number(formData.max_rig_length) : null,
      camper_capacity: formData.camper_capacity ? Number(formData.camper_capacity) : null,
      minimum_capacity: formData.minimum_capacity ? Number(formData.minimum_capacity) : null,
      last_meter_reading: formData.current_meter_reading ? Number(formData.current_meter_reading) : null,
      electric: formData.electric,
      water: formData.water,
      has_linens: formData.has_linens,
      is_bookable: formData.is_bookable,
      notes: formData.notes || null,
      checkin_policy_additions: formData.checkin_policy_additions || null,
      prices_attributes: pricesData,
      vendor_calendars_attributes: vendorCalendarsData,
    }

    // Add boolean features
    features.forEach((field) => {
      if (formData[field] !== undefined) {
        dataToSend[field] = formData[field]
      }
    })

    // Add bed features
    bedFeatures.forEach((field) => {
      if (formData[field] !== undefined) {
        dataToSend[field] = formData[field] || null
      }
    })

    await SitesApi.update(formData.id, dataToSend)
      .then(() =>
        showNotification({
          type: "success",
          title: "Changes saved!",
          description: `Successfully updated ${formData.name}`,
        }),
      )
      .catch((e) => {
        console.error("Error updating site:", e)
        showNotification({
          type: "error",
          title: "Oops! Something went wrong.",
        })
      })
    setEditingSite(null)
    refetchSites()
  }

  const persistOrderIfDirty = async () => {
    if (isDirty) await handleSave()
  }

  const openEdit = async (site: SiteProps) => {
    await persistOrderIfDirty()
    setEditingSite(site)
  }

  const onDuplicate = async (site: SiteProps) => {
    await persistOrderIfDirty()
    setConfirm(null)
    const { site: newSite } = await SitesApi.duplicate(site.id)
    showNotification({
      type: "success",
      title: `${newSite.name} added to the bottom of your RV Sites`,
    })
    refetchSites()
  }
  const onRemove = async (site: SiteProps) => {
    await persistOrderIfDirty()
    setConfirm(null)
    await SitesApi.remove(site.id)
    showNotification({ type: "success", title: `${site.name} deleted` })
    refetchSites()
  }

  return (
    <div className="space-y-6">
      <div id="save-buttons-sentinel" style={{ height: -50 }}></div>
      <div
        ref={saveButtonsRef}
        className={`sticky z-20 top-[71px] p-2 ${isStuck ? 'bg-white' : 'bg-transparent'
          }`}
      >
        <SaveButtons
          isDirty={isDirty}
          isLoading={pending}
          errors={{}}
          handleSave={handleSave}
          handleCancel={() => {
            setSites(originalSites)
            setStyleOrder(originalStyleOrder)
          }}
          handleSubmit={handleSave}
        />
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleStyleDragStart}
        onDragEnd={handleStyleDragEnd}
      >
        <SortableContext items={styleOrder} strategy={verticalListSortingStrategy}>
          {groups.map((group) => (
            <SortableStyle
              key={group.style.id}
              style={group.style}
              isCollapsed={isDraggingStyle}
              siteCount={group.sites.length}
            >
              {!isDraggingStyle && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleDragEnd(e, group.style.id)}
                >
                  <SortableContext items={group.sites.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                    <ul className="space-y-1">
                      {group.sites.map((site) => (
                        <SortableSite
                          key={site.id}
                          site={site}
                          campingStyle={group.style}
                          lengthUnit={camp?.length_unit || "ft"}
                          onEdit={() => openEdit(site)}
                          onDuplicate={() => setConfirm({ action: "duplicate", site })}
                          onRemove={() => setConfirm({ action: "remove", site })}
                          actionsDisabled={isDirty}
                        />
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>
              )}
            </SortableStyle>
          ))}
        </SortableContext>
      </DndContext>
      {editingSite && (
        <Modal
          open
          onClose={() => setEditingSite(null)}
          className="h-full min-h-full max-h-full flex flex-col md:!min-h-[90vh] md:!max-h-[90vh] pb-24 md:pb-4"
        >
          <div className="flex-1 pt-8 px-6 md:overflow-y-auto">
            <EditSiteForm
              ref={editSiteFormRef}
              site={editingSite}
              camp={camp}
              isProcessing={pending}
              errorMessage=""
              setErrorMessage={() => { }}
              setIsSiteSaveDisabled={() => { }}
              onNotesChange={() => { }}
              onCheckinPolicyAdditionsChange={() => { }}
              showFeatures={false}
              setShowFeatures={() => { }}
              setIsSlideoverOpen={() => { }}
            />
          </div>
          <div
            className="flex justify-end gap-2 px-4 pt-4 pb-0 border-t bg-white
              fixed bottom-0 left-0 w-full z-10
              md:sticky md:bottom-0 md:w-full"
          >
            <Button variant="gray" type="button" onClick={() => setEditingSite(null)}>
              Cancel
            </Button>
            <Button
              variant="green"
              type="button"
              onClick={() => {
                editSiteFormRef.current?.handleSubmit(handleEditSubmit)
              }}
            >
              Save
            </Button>
          </div>
        </Modal>
      )}
      {confirm && (
        <Modal open onClose={() => setConfirm(null)} width="max-w-lg">
          <div className="pt-1">
            <p className="mb-4 text-lg font-semibold">
              {confirm.action === "duplicate" ? "Duplicate" : "Remove"} {confirm.site.name}?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="gray" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant={confirm.action === "remove" ? "red" : "green"}
                onClick={() => (confirm.action === "duplicate" ? onDuplicate(confirm.site) : onRemove(confirm.site))}
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
